import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import MovieCard from '@/components/base/MovieCard';
import PlaybackModal from '@/components/base/PlaybackModal';
import SectionHeading from '@/components/base/SectionHeading';
import { formatDate, formatDuration, getMovieById, getRelatedMovies } from '@/lib/catalog';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function MovieDetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id);
  const [playing, setPlaying] = useState(false);
  const [toast, setToast] = useState('');

  useDocumentMeta({
    title: movie ? `电影《${movie.title}》详情页 - 午夜电影网` : '影片未找到 - 午夜电影网',
    description: movie
      ? `《${movie.title}》（${movie.enTitle}）${movie.year} 年${movie.region}${movie.genres.join('、')}电影，${movie.director}执导，${movie.cast.slice(0, 3).join('、')}主演。${movie.synopsis.slice(0, 60)}`
      : '抱歉，你没有找到想看的影片。可以返回片库继续浏览，或使用搜索寻找其他电影。',
    keywords: movie ? `${movie.title},${movie.genres.join(',')},电影详情,午夜电影网` : '午夜电影网,影片未找到',
  });

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!movie) {
    return (
      <div className="shell pb-20 pt-32">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '电影分类', to: '/movies' }, { label: '影片未找到' }]} />
        <div className="mt-8">
          <EmptyState
            icon="ri-film-line"
            title="影片未找到"
            description="这部影片可能已经下架，或者链接地址有误。你可以回到片库继续浏览，也可以按类型重新寻找。"
            actionLabel="返回电影片库"
            actionTo="/movies"
          />
        </div>
      </div>
    );
  }

  const related = getRelatedMovies(movie, 4);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast('链接已复制，可以分享给朋友了');
    } catch {
      setToast('复制失败，请手动复制浏览器地址');
    }
  };

  return (
    <div>
      <section className="relative overflow-hidden pb-12 pt-24 md:pb-16 md:pt-32">
        <div className="absolute inset-0">
          <img
            src={movie.backdrop}
            alt={`《${movie.title}》背景剧照`}
            className="h-full w-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-background-50/70" />
        </div>

        <div className="shell relative">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '电影分类', to: '/movies' },
              { label: movie.title },
            ]}
          />

          <div className="mt-6 grid grid-cols-1 gap-7 md:grid-cols-[240px_1fr] md:gap-9 lg:grid-cols-[280px_1fr]">
            <div className="w-full max-w-[220px] md:max-w-none">
              <div className="aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-300/60 bg-background-200">
                <img
                  src={movie.poster}
                  alt={`电影《${movie.title}》海报`}
                  title={`${movie.title} 高清电影海报`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="mt-3 flex items-center justify-between rounded-lg border border-background-200 bg-background-100/80 px-3 py-2 text-[11px] text-foreground-500 backdrop-blur">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-primary-500" aria-hidden="true"></i>
                  <strong className="font-semibold text-foreground-900">{movie.rating.toFixed(1)}</strong> / 10
                </span>
                <span>热度 {movie.popularity}</span>
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-primary-500 px-2 py-0.5 text-[11px] font-medium text-background-50">高清</span>
                <span className="rounded border border-background-300/70 bg-background-50/40 px-2 py-0.5 text-[11px] text-foreground-600 backdrop-blur">
                  演示站点 · 无真实片源
                </span>
              </div>

              <h1 className="mt-3 font-heading text-4xl font-semibold leading-tight tracking-wide text-foreground-950 md:text-5xl">
                {movie.title}
              </h1>
              <p className="mt-2 text-xs tracking-[0.28em] text-primary-500">{movie.enTitle}</p>
              <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-base">{movie.tagline}</p>

              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-600 md:text-sm">
                <span>{movie.year}</span>
                <span className="h-3 w-px bg-background-400/50" />
                <span>{movie.region}</span>
                <span className="h-3 w-px bg-background-400/50" />
                <span>{formatDuration(movie.duration)}</span>
                <span className="h-3 w-px bg-background-400/50" />
                <span>{movie.genres.join(' / ')}</span>
              </div>

              <div className="mt-5 space-y-1.5 text-sm text-foreground-600">
                <p>
                  <span className="text-foreground-500">导演：</span>
                  {movie.director}
                </p>
                <p>
                  <span className="text-foreground-500">主演：</span>
                  {movie.cast.join(' / ')}
                </p>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setPlaying(true)}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-fill text-lg" aria-hidden="true"></i>
                  在线播放（演示）
                </button>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/80 bg-background-50/30 px-5 py-3 text-sm text-foreground-800 backdrop-blur transition-colors hover:border-primary-500/70 hover:text-primary-500"
                >
                  <i className="ri-link" aria-hidden="true"></i>
                  复制链接
                </button>
                <div className="flex items-center gap-2">
                  {[
                    { icon: 'ri-weibo-line', label: '微博' },
                    { icon: 'ri-wechat-line', label: '微信' },
                    { icon: 'ri-share-forward-line', label: '更多' },
                  ].map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => setToast(`${item.label}分享为界面演示，暂未接入真实社交接口`)}
                      aria-label={`分享到${item.label}`}
                      title={`分享到${item.label}`}
                      className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/70 text-foreground-600 transition-colors hover:border-primary-500/70 hover:text-primary-500"
                    >
                      <i className={item.icon} aria-hidden="true"></i>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100 py-12 md:py-16">
        <div className="shell grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <h2 className="flex items-center gap-2 font-heading text-2xl tracking-wide text-foreground-950">
              <span className="h-4 w-1 rounded-full bg-primary-500" />
              剧情简介
            </h2>
            <p className="mt-4 text-sm leading-8 text-foreground-600 md:text-[15px] md:leading-9">{movie.synopsis}</p>

            <h2 className="mt-10 flex items-center gap-2 font-heading text-2xl tracking-wide text-foreground-950">
              <span className="h-4 w-1 rounded-full bg-primary-500" />
              影片看点
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {movie.tags.map((tag, index) => (
                <div key={tag} className="rounded-md border border-background-200 bg-background-50 p-4">
                  <p className="font-heading text-2xl text-primary-500/80">{String(index + 1).padStart(2, '0')}</p>
                  <p className="mt-2 text-sm text-foreground-800">{tag}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-md border border-background-200 bg-background-50 p-4">
              <p className="flex items-start gap-2 text-xs leading-6 text-foreground-500">
                <i className="ri-information-line mt-1 text-primary-500" aria-hidden="true"></i>
                本页播放按钮仅用于展示产品交互，午夜电影网不提供真实影片片源、播放地址或下载内容。影片信息与评分均为演示数据。
              </p>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-xl tracking-wide text-foreground-950">影片信息</h2>
              <dl className="mt-4 space-y-3 text-xs">
                {[
                  { label: '中文片名', value: movie.title },
                  { label: '英文片名', value: movie.enTitle },
                  { label: '导演', value: movie.director },
                  { label: '主演', value: movie.cast.join(' / ') },
                  { label: '类型', value: movie.genres.join(' / ') },
                  { label: '制片地区', value: movie.region },
                  { label: '上映年份', value: `${movie.year}` },
                  { label: '片长', value: formatDuration(movie.duration) },
                  { label: '上映日期', value: formatDate(movie.releaseDate) },
                ].map((row) => (
                  <div key={row.label} className="flex items-start justify-between gap-4 border-b border-background-200/70 pb-3 last:border-none last:pb-0">
                    <dt className="shrink-0 text-foreground-500">{row.label}</dt>
                    <dd className="text-right text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-4 rounded-lg border border-primary-500/25 bg-primary-500/10 p-5">
              <p className="eyebrow text-[10px] text-primary-500">Watch note</p>
              <h2 className="mt-2 font-heading text-xl tracking-wide text-foreground-950">关于播放</h2>
              <p className="mt-2 text-xs leading-6 text-foreground-600">
                这是前端演示版本。点击播放会看到一段说明，而不是真实的视频流。你可以先把它当作一个片单工具，用来挑片、看资料、做决定。
              </p>
              <button
                type="button"
                onClick={() => setPlaying(true)}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-md border border-primary-500/60 py-2.5 text-xs text-primary-500 transition-colors hover:bg-primary-500/10"
              >
                <i className="ri-play-circle-line" aria-hidden="true"></i>
                查看播放演示
              </button>
            </div>
          </aside>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="shell">
          <SectionHeading
            eyebrow="You may also like"
            title="相关推荐"
            description="根据类型与口碑为你挑选的同类影片。"
            actionLabel="更多同类"
            actionTo="/movies"
          />
          <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} showGenres />
            ))}
          </div>

          <div className="mt-8">
            <Link to="/news" className="text-xs text-foreground-500 transition-colors hover:text-primary-500">
              想了解更多幕后故事？去看最新电影资讯 →
            </Link>
          </div>
        </div>
      </section>

      <PlaybackModal open={playing} movie={movie} onClose={() => setPlaying(false)} />

      {toast && (
        <div className="animate-fade-in fixed inset-x-4 bottom-6 z-[70] mx-auto w-fit max-w-[92%] rounded-md border border-primary-500/40 bg-background-100 px-4 py-2.5 text-center text-xs text-foreground-800">
          {toast}
        </div>
      )}
    </div>
  );
}